import { apis } from '../../../core/services/params';
import axiosInstance from '../../../core/services/axiosConfig';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import StitchLayout from '../../components/Crm/CrmLayout';
import { Spin, Button, Table, DatePicker, Select } from 'antd';
import {
    ReloadOutlined,
    WarningOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    MailOutlined,
    PhoneOutlined,
    GlobalOutlined,
    FacebookOutlined,
    SendOutlined,
    UserOutlined,
    FireOutlined,
    HourglassOutlined,
    CalendarOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    MinusOutlined,
    FilterOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, Cell, LabelList,
} from 'recharts';


const { RangePicker } = DatePicker;

interface DashboardData {
    by_source: Record<string, number>;
    by_status: Record<string, number>;
    by_department?: Record<string, number>;
    date_wise: Record<string, {
        by_source: Record<string, { by_status: Record<string, number>; total_count: number }>;
        sla_breached: number;
        total: number;
    }>;
    source_then_status: Record<string, Record<string, number>>;
    status_then_source: Record<string, Record<string, number>>;
    total_tickets: number;
    interval?: string;
    timezone?: string;
    avg_first_response_time_minutes?: {
        avg_hours: number;
        avg_minutes: number;
        tickets_with_data: number;
    };
    avg_resolution_time_minutes?: {
        avg_hours: number;
        avg_minutes: number;
        tickets_with_data: number;
    };
    csat_analysis?: {
        100?: number;
        103?: number;
        total_tickets_with_csat?: number;
        [key: string]: any;
    };
    by_l1_category?: {
        l1_category_code: string;
        l1_category_image_url: string;
        l1_category_link: string;
        l1_category_name: string;
        ticket_count: number;
    }[];
    open_ticket_ageing?: Record<string, number>;
    week_wise_pendency?: {
        current_week: { open_tickets_count: number };
        last_week: { open_tickets_count: number };
    };
}


// Status color mapping
const STATUS_COLORS: Record<string, string> = {
    'Open': '#f59e0b',
    'Closed': '#10b981',
    'Resolved': '#3b82f6',
    'Pending by customer': '#8b5cf6',
    'Pending by Moglix': '#ef4444',
    'Responded': '#06b6d4',
    'Chatbot - Resolved': '#84cc16',
};

// Source color mapping
const SOURCE_COLORS: Record<string, string> = {
    'Email': '#3b82f6',
    'Phone': '#10b981',
    'Portal': '#8b5cf6',
    'Facebook': '#f59e0b',
    'Outbound Email': '#ef4444',
};

// Source icon mapping
const SOURCE_ICONS: Record<string, React.ReactNode> = {
    'Email': <MailOutlined />,
    'Phone': <PhoneOutlined />,
    'Portal': <GlobalOutlined />,
    'Facebook': <FacebookOutlined />,
    'Outbound Email': <SendOutlined />,
};

// Reverse map: source label → numeric source code (matches Freshdesk SOURCE_MAP)
const LABEL_TO_SOURCE_CODE: Record<string, number> = {
    'Email': 1,
    'Portal': 2,
    'Phone': 3,
    'Forum': 4,
    'Twitter': 5,
    'Facebook': 6,
    'Outbound Email': 10,
};

// Reverse map: status label → numeric status code (case-insensitive lookup handled at call site)
const LABEL_TO_STATUS_CODE: Record<string, number> = {
    'open': 2,
    'pending': 3,
    'resolved': 4,
    'closed': 5,
    'chatbot - resolved': 11,
    'responded': 12,
    'pending by customer': 13,
    'pending by moglix': 14,
    'resolved email': 15,
};

// Horizontal bar component with label and value
const HBar = ({ label, value, max, color, icon }: { label: string; value: number; max: number; color: string; icon?: React.ReactNode }) => {
    const pct = max > 0 ? (value / max) * 100 : 0;
    return (
        <div className="flex items-center gap-3 mb-3 last:mb-0">
            <div className="flex items-center gap-1.5 w-36 flex-shrink-0 text-xs text-gray-600 dark:text-gray-300 font-medium">
                {icon && <span style={{ color }} className="text-xs">{icon}</span>}
                <span className="truncate">{label}</span>
            </div>
            <div className="flex-1 h-5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                    className="h-full rounded-full flex items-center pl-2 text-[10px] text-white font-semibold transition-all duration-700"
                    style={{ width: `${Math.max(pct, 4)}%`, backgroundColor: color }}
                >
                    {value > 5 ? value : ''}
                </div>
            </div>
            <span className="w-10 text-right text-xs font-bold text-gray-700 dark:text-gray-300">{value}</span>
        </div>
    );
};

// Format minutes to hours and minutes display
const formatMinutesToHoursAndMinutes = (minutes: number): string => {
    if (minutes === 0) return '0 min';
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours === 0) return `${remainingMinutes} min`;
    if (remainingMinutes === 0) return `${hours}h`;
    return `${hours}h ${remainingMinutes}m`;
};

// Format date bucket label based on interval from API
const formatBucketLabel = (key: string, interval?: string): string => {
    const iv = (interval || '').toLowerCase();
    if (iv.includes('hour')) return moment(key).format('HH:mm');
    if (iv.includes('month')) return moment(key).format('MMM YY');
    if (iv.includes('week')) return moment(key).format('DD MMM');
    return moment(key).format('DD/MM');
};


// Main CRM Dashboard page component
const QUICK_FILTERS = [
    { label: 'Today', days: 0 },
    { label: 'Yesterday', days: -1 },
    { label: 'Last 3 Days', days: 3 },
    { label: 'Last 7 Days', days: 7 },
    { label: 'Last 14 Days', days: 14 },
    { label: 'Last 30 Days', days: 30 },
    { label: 'Last 90 Days', days: 90 },
];

const CrmDashboard = () => {
    const navigate = useNavigate();
    const getInitialPickerValue = (): [Dayjs, Dayjs] => {
        try {
            const storedStart = sessionStorage.getItem('dashboard_date_filter_start');
            const storedEnd = sessionStorage.getItem('dashboard_date_filter_end');
            if (storedStart && storedEnd) {
                return [dayjs(storedStart), dayjs(storedEnd)];
            }
        } catch (e) {
            // ignore
        }
        return [dayjs().startOf('day'), dayjs().endOf('day')];
    };

    const getInitialQuickFilter = (): string => {
        return sessionStorage.getItem('dashboard_quick_filter') || 'Today';
    };

    const [pickerValue, setPickerValue] = useState<[Dayjs, Dayjs]>(getInitialPickerValue());
    const [activeQuickFilter, setActiveQuickFilter] = useState<string>(getInitialQuickFilter());
    const [selectedDepartment, setSelectedDepartment] = useState<string>('');
    const [data, setData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchData = async (start?: Dayjs, end?: Dayjs, department?: string) => {
        const s = (start ?? pickerValue[0]).startOf('day').toISOString();
        const e = (end ?? pickerValue[1]).endOf('day').toISOString();
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({ start_date: s, end_date: e });
            const dept = department !== undefined ? department : selectedDepartment;
            if (dept) params.set('department', dept);
            const res = await axiosInstance.get(`${apis.FRESHDESK_TICKETS_DASHBOARD}?${params}`);
            const json = res.data;
            if (json.status && json.data) {
                setData(json.data);
            } else {
                setError('Unexpected response from API.');
            }
        } catch {
            setError('Failed to load dashboard.');
        } finally {
            setLoading(false);
        }
    };

    const handleRangeChange = (vals: any) => {
        if (!vals || !vals[0] || !vals[1]) return;
        setPickerValue([vals[0], vals[1]]);
        setActiveQuickFilter('');
        
        sessionStorage.setItem('dashboard_date_filter_start', vals[0].toISOString());
        sessionStorage.setItem('dashboard_date_filter_end', vals[1].toISOString());
        sessionStorage.removeItem('dashboard_quick_filter');

        fetchData(vals[0], vals[1], selectedDepartment);
    };

    const handleQuickFilter = (filter: typeof QUICK_FILTERS[0]) => {
        let start: Dayjs;
        let end: Dayjs;
        if (filter.days === 0) {
            start = dayjs().startOf('day');
            end = dayjs().endOf('day');
        } else if (filter.days === -1) {
            start = dayjs().subtract(1, 'day').startOf('day');
            end = dayjs().subtract(1, 'day').endOf('day');
        } else {
            start = dayjs().subtract(filter.days - 1, 'day').startOf('day');
            end = dayjs().endOf('day');
        }
        setPickerValue([start, end]);
        setActiveQuickFilter(filter.label);
        
        sessionStorage.setItem('dashboard_date_filter_start', start.toISOString());
        sessionStorage.setItem('dashboard_date_filter_end', end.toISOString());
        sessionStorage.setItem('dashboard_quick_filter', filter.label);

        fetchData(start, end, selectedDepartment);
    };

    const handleResetFilter = () => {
        const start = dayjs().startOf('day');
        const end = dayjs().endOf('day');
        
        setPickerValue([start, end]);
        setActiveQuickFilter('Today');
        setSelectedDepartment('');
        
        sessionStorage.removeItem('dashboard_date_filter_start');
        sessionStorage.removeItem('dashboard_date_filter_end');
        sessionStorage.removeItem('dashboard_quick_filter');
        
        fetchData(start, end, '');
    };

    // Shared helper — merges any extra filter fields with the current dashboard date range
    const pushFilter = (extra: Record<string, any>) => {
        const filter = {
            created_at: [
                pickerValue[0].startOf('day').toISOString(),
                pickerValue[1].endOf('day').toISOString(),
            ],
            ...extra,
        };
        sessionStorage.setItem('freshdesk_filters', JSON.stringify(filter));
        sessionStorage.setItem('freshdesk_pagination', JSON.stringify({ page: 1, pages: 1, per_page: 10, total: 0 }));
        navigate('/crm/freshdesk-tickets');
    };

    const navigateToCategory = (categoryCode: string) => pushFilter({ category_code: categoryCode });

    const navigateToStatus = (statusCodes: number[]) => pushFilter({ status: statusCodes });

    const navigateToSource = (sourceLabel: string) => {
        const sourceCode = LABEL_TO_SOURCE_CODE[sourceLabel];
        if (!sourceCode) return;
        pushFilter({ source: sourceCode });
    };

    const navigateToDepartment = (department: string) => pushFilter({ department });

    const navigateToStatusByLabel = (statusLabel: string) => {
        const code = LABEL_TO_STATUS_CODE[statusLabel.toLowerCase()];
        if (!code) return;
        pushFilter({ status: [code] });
    };

    useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const stats = useMemo(() => {
        if (!data) return null;
        const byStatus = data.by_status || {};
        let customerSatisfaction = 0;
        if (data.csat_analysis && typeof data.csat_analysis === 'object') {
            const satisfied = data.csat_analysis[103] || 0;
            const notSatisfied = data.csat_analysis[100] || 0;
            const total = data.csat_analysis.total_tickets_with_csat || 0;
            if (total > 0) {
                customerSatisfaction = ((satisfied * 5) + (notSatisfied * 1)) / total;
                customerSatisfaction = Math.round(customerSatisfaction * 100) / 100; // round to 2 decimals
            }
        }
        const _open = (byStatus['Open'] || 0) + (byStatus['Pending'] || 0) + (byStatus['Responded'] || 0) + (byStatus['Pending by customer'] || 0) + (byStatus['Pending by Moglix'] || 0)
        const _closed = (byStatus['Closed'] || 0) + (byStatus['Resolved'] || 0) + (byStatus['Chatbot - Resolved'] || 0) + (byStatus["Resolved Email"] || 0)
        return {
            total: data.total_tickets || 0,
            open: _open,
            closed: _closed,
            avgResponseTime: Math.round(data.avg_first_response_time_minutes?.avg_minutes || 0),
            avgResolutionTime: Math.round(data.avg_resolution_time_minutes?.avg_minutes || 0),
            slaCompliance: 87.5, // percentage (still mock - awaiting API implementation)
            customerSatisfaction,
            weekOverWeekChange: 12.5 // percentage increase (still mock - awaiting API implementation)
        };
    }, [data]);

    const statusEntries = useMemo(() =>
        data ? Object.entries(data.by_status || {}).sort((a, b) => b[1] - a[1]) : [], [data]);

    const sourceEntries = useMemo(() =>
        data ? Object.entries(data.by_source || {}).sort((a, b) => b[1] - a[1]) : [], [data]);

    // By Department
    const departmentEntries = useMemo(() =>
        data ? Object.entries(data.by_department || {}).sort((a, b) => Number(b[1]) - Number(a[1])) as [string, number][] : [], [data]);

    const dateEntries = useMemo(() =>
        data ? Object.entries(data.date_wise || {}).sort((a, b) => a[0].localeCompare(b[0])) : [], [data]);

    const l1CategoryEntries = useMemo(() =>
        data?.by_l1_category ? [...data.by_l1_category].sort((a, b) => b.ticket_count - a.ticket_count) : [], [data]);

    const maxStatus = useMemo(() => Math.max(...statusEntries.map(([, v]) => v), 1), [statusEntries]);
    const maxSource = useMemo(() => Math.max(...sourceEntries.map(([, v]) => v), 1), [sourceEntries]);
    const maxDepartment = useMemo(() => Math.max(...departmentEntries.map(([, v]) => Number(v)), 1), [departmentEntries]);

    // Source × Status matrix table columns
    const matrixColumns = useMemo(() => {
        if (!data) return [];
        // Fallback to empty object to avoid Object.keys(undefined) crash
        const statuses = Object.keys(data?.by_status || {});
        return [
            { title: 'Source', dataIndex: 'source', key: 'source', fixed: 'left' as const, width: 130, render: (v: string) => <span className="font-semibold text-gray-700 dark:text-gray-300">{v}</span> },
            ...statuses.map(s => ({
                title: <span style={{ color: STATUS_COLORS[s] || '#666', fontSize: 11 }}>{s}</span>,
                dataIndex: s,
                key: s,
                width: 110,
                render: (v: number) => v ? <span className="font-medium text-gray-700 dark:text-gray-300">{v}</span> : <span className="text-gray-300 dark:text-gray-600">—</span>,
            })),
        ];
    }, [data]);

    // Source × Status matrix table rows with null guard
    const matrixData = useMemo(() => {
        if (!data) return [];
        return Object.entries(data.source_then_status || {}).map(([source, statusMap]) => ({
            key: source, source, ...statusMap,
        }));
    }, [data]);

    return (
        <StitchLayout>
            <div className="max-w-7xl mx-auto">
                {/* Dashboard top header with date range picker */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
                    <div className="flex-1">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-0.5">Support Dashboard</h1>
                        <p className="text-xs text-gray-400 dark:text-gray-500">Freshdesk ticket analytics</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <RangePicker
                            value={pickerValue}
                            onChange={handleRangeChange}
                            disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
                            allowClear={false}
                            format="YYYY-MM-DD"
                        />
                        <Select
                            allowClear
                            placeholder="All Departments"
                            style={{ minWidth: 180 }}
                            value={selectedDepartment || undefined}
                            onChange={(val: string | undefined) => {
                                const dept = val ?? '';
                                setSelectedDepartment(dept);
                                fetchData(pickerValue[0], pickerValue[1], dept);
                            }}
                            options={[
                                { label: 'Sahara Voice Bot', value: 'Sahara Voice Bot' },
                                { label: 'Setu Chat Bot', value: 'Setu Chat Bot' },
                                { label: 'ChatBot', value: 'ChatBot' },
                                { label: 'CC Team', value: 'CC Team' },
                                { label: 'Sales Team', value: 'Sales Team' },
                            ]}
                        />
                        <Button icon={<FilterOutlined />} onClick={handleResetFilter}>Reset Filters</Button>
                        <Button icon={<ReloadOutlined />} onClick={() => fetchData()} loading={loading}>Refresh</Button>
                    </div>
                </div>

                {/* Quick filter pills */}
                <div className="flex flex-wrap items-center gap-1.5 mb-5">
                    <span className="text-xs text-gray-500 font-medium mr-1">Quick:</span>
                    {QUICK_FILTERS.map(f => {
                        const isActive = activeQuickFilter === f.label;
                        return (
                            <button
                                key={f.label}
                                onClick={() => handleQuickFilter(f)}
                                className="px-3 py-1 rounded-full text-xs font-semibold border transition-all duration-150 cursor-pointer"
                                style={isActive ? {
                                    background: '#4f46e5',
                                    color: '#fff',
                                    borderColor: '#4f46e5',
                                    boxShadow: '0 2px 8px rgba(79,70,229,0.25)',
                                } : {
                                    background: 'transparent',
                                    color: '#6b7280',
                                    borderColor: '#e5e7eb',
                                }}
                            >
                                {f.label}
                            </button>
                        );
                    })}
                </div>

                {loading && <div className="flex justify-center py-24"><Spin size="large" /></div>}
                {error && <div className="text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">{error}</div>}

                {!loading && !error && stats && data && (
                    <>
                        {/* KPI cards — all 7 in a single horizontal row */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-6">

                            {/* Total Tickets */}
                            <div
                                onClick={() => pushFilter({})}
                                className="relative rounded-xl p-3 bg-white/70 dark:bg-gray-900/70 backdrop-blur-md shadow border border-blue-100 dark:border-blue-900 group overflow-hidden cursor-pointer hover:border-blue-400 hover:shadow-md transition-all duration-200"
                            >
                                <div className="absolute -top-4 -right-4 w-14 h-14 bg-gradient-to-br from-blue-400/30 to-blue-700/40 rounded-full blur-xl opacity-40 group-hover:opacity-60 transition-all duration-300" />
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 text-white text-base flex-shrink-0">
                                        <UserOutlined />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-blue-700 dark:text-blue-200 text-[10px] font-bold tracking-wide mb-0.5 truncate">Total Tickets</p>
                                        <p className="text-xl font-extrabold tracking-tight text-gray-900 dark:text-white leading-none">{stats.total.toLocaleString()}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Open Tickets */}
                            <div
                                onClick={() => navigateToStatus([2, 3, 13, 14])}
                                className="relative rounded-xl p-3 bg-white/70 dark:bg-gray-900/70 backdrop-blur-md shadow border border-yellow-100 dark:border-yellow-900 group overflow-hidden cursor-pointer hover:border-yellow-400 hover:shadow-md transition-all duration-200"
                            >
                                <div className="absolute -top-4 -right-4 w-14 h-14 bg-gradient-to-br from-yellow-300/30 to-orange-500/40 rounded-full blur-xl opacity-40 group-hover:opacity-60 transition-all duration-300" />
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br from-yellow-400 to-orange-500 text-white text-base flex-shrink-0">
                                        <WarningOutlined />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-yellow-700 dark:text-yellow-200 text-[10px] font-bold tracking-wide mb-0.5">Open</p>
                                        <p className="text-xl font-extrabold tracking-tight text-gray-900 dark:text-white leading-none">{stats.open.toLocaleString()}</p>
                                        <p className="text-yellow-500 dark:text-yellow-300 text-[10px] mt-0.5">{stats.total ? ((stats.open / stats.total) * 100).toFixed(1) : 0}% of total</p>
                                    </div>
                                </div>
                            </div>

                            {/* Closed Tickets */}
                            <div
                                onClick={() => navigateToStatus([4, 5, 11, 12, 15])}
                                className="relative rounded-xl p-3 bg-white/70 dark:bg-gray-900/70 backdrop-blur-md shadow border border-green-100 dark:border-green-900 group overflow-hidden cursor-pointer hover:border-green-400 hover:shadow-md transition-all duration-200"
                            >
                                <div className="absolute -top-4 -right-4 w-14 h-14 bg-gradient-to-br from-green-300/30 to-green-700/40 rounded-full blur-xl opacity-40 group-hover:opacity-60 transition-all duration-300" />
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br from-green-400 to-green-700 text-white text-base flex-shrink-0">
                                        <CheckCircleOutlined />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-green-700 dark:text-green-200 text-[10px] font-bold tracking-wide mb-0.5">Closed</p>
                                        <p className="text-xl font-extrabold tracking-tight text-gray-900 dark:text-white leading-none">{stats.closed.toLocaleString()}</p>
                                        <p className="text-green-500 dark:text-green-300 text-[10px] mt-0.5">{stats.total ? ((stats.closed / stats.total) * 100).toFixed(1) : 0}% of total</p>
                                    </div>
                                </div>
                            </div>

                            {/* Avg Response Time */}
                            <div className="flex items-center gap-2 rounded-xl bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/20 p-3 shadow border border-green-100 dark:border-green-700">
                                <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br from-green-400 to-green-600 text-white text-base shadow flex-shrink-0">
                                    <ClockCircleOutlined />
                                </div>
                                <div className="flex-grow min-w-0">
                                    <p className="text-green-700 dark:text-green-400 text-[10px] font-semibold truncate">Avg Response Time</p>
                                    <p className="text-xl font-extrabold text-gray-900 dark:text-white leading-none">{formatMinutesToHoursAndMinutes(stats.avgResponseTime)}</p>
                                    <div className="w-full bg-green-200 dark:bg-green-900 rounded-full h-1 mt-1 overflow-hidden">
                                        <div className="bg-green-500 h-1 rounded-full animate-pulse" style={{ width: stats.avgResponseTime > 60 ? '60%' : '85%' }} />
                                    </div>
                                    <p className="text-[9px] text-green-600 dark:text-green-300 mt-0.5">Target: 30 min</p>
                                </div>
                            </div>

                            {/* Avg Resolution Time */}
                            <div className="flex items-center gap-2 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/20 p-3 shadow border border-blue-100 dark:border-blue-700">
                                <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 text-white text-base shadow flex-shrink-0">
                                    <CheckCircleOutlined />
                                </div>
                                <div className="flex-grow min-w-0">
                                    <p className="text-blue-700 dark:text-blue-400 text-[10px] font-semibold truncate">Avg Resolution Time</p>
                                    <p className="text-xl font-extrabold text-gray-900 dark:text-white leading-none">{formatMinutesToHoursAndMinutes(stats.avgResolutionTime)}</p>
                                    <div className="w-full bg-blue-200 dark:bg-blue-900 rounded-full h-1 mt-1 overflow-hidden">
                                        <div className="bg-blue-500 h-1 rounded-full animate-pulse" style={{ width: stats.avgResolutionTime > 180 ? '70%' : '90%' }} />
                                    </div>
                                    <p className="text-[9px] text-blue-600 dark:text-blue-300 mt-0.5">Target: 3h</p>
                                </div>
                            </div>

                            {/* Customer Satisfaction */}
                            <div className="flex items-center gap-2 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/20 p-3 shadow border border-purple-100 dark:border-purple-700">
                                <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br from-purple-400 to-purple-600 text-white text-base shadow flex-shrink-0">
                                    <CheckCircleOutlined />
                                </div>
                                <div className="flex-grow min-w-0">
                                    <p className="text-purple-700 dark:text-purple-400 text-[10px] font-semibold truncate">Customer Satisfaction</p>
                                    <p className="text-xl font-extrabold text-gray-900 dark:text-white leading-none">{stats.customerSatisfaction}/5.0</p>
                                    <div className="flex space-x-0.5 mt-1">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <div
                                                key={star}
                                                className={`w-3 h-3 rounded-full transition-all duration-300 ${star <= Math.floor(stats.customerSatisfaction) ? 'bg-yellow-400 scale-105 shadow' : 'bg-gray-200 dark:bg-gray-600'}`}
                                            />
                                        ))}
                                    </div>
                                    <p className="text-[9px] text-purple-600 dark:text-purple-300 mt-0.5">Based on feedback</p>
                                </div>
                            </div>

                            {/* Pendency — always reflects weekly open backlog, not filtered */}
                            {(() => {
                                const pendency = data.week_wise_pendency;
                                const thisWeek = pendency?.current_week?.open_tickets_count ?? null;
                                const lastWeek = pendency?.last_week?.open_tickets_count ?? null;
                                const diff = thisWeek !== null && lastWeek !== null ? thisWeek - lastWeek : null;
                                const isUp = diff !== null && diff > 0;
                                const isDown = diff !== null && diff < 0;
                                const isFlat = diff === 0;
                                return (
                                    <div className="flex items-start gap-2 rounded-xl bg-gradient-to-br from-rose-50 to-orange-50 dark:from-rose-900/30 dark:to-orange-900/20 p-3 shadow border border-rose-100 dark:border-rose-700">
                                        <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br from-rose-500 to-orange-500 text-white text-base shadow flex-shrink-0 mt-0.5">
                                            <CalendarOutlined />
                                        </div>
                                        <div className="flex-grow min-w-0">
                                            <p className="text-rose-700 dark:text-rose-400 text-[10px] font-bold tracking-wide mb-1 truncate">Pendency</p>
                                            {/* Current week */}
                                            <div className="flex items-baseline gap-1">
                                                <p className="text-xl font-extrabold text-gray-900 dark:text-white leading-none">
                                                    {thisWeek !== null ? thisWeek : '—'}
                                                </p>
                                                {diff !== null && (
                                                    <span
                                                        className="inline-flex items-center gap-0.5 text-[10px] font-bold"
                                                        style={{ color: isUp ? '#ef4444' : isDown ? '#22c55e' : '#9ca3af' }}
                                                    >
                                                        {isUp && <ArrowUpOutlined style={{ fontSize: 9 }} />}
                                                        {isDown && <ArrowDownOutlined style={{ fontSize: 9 }} />}
                                                        {isFlat && <MinusOutlined style={{ fontSize: 9 }} />}
                                                        {Math.abs(diff)}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-[9px] text-rose-500 dark:text-rose-300 mt-0.5 leading-tight">This week open</p>
                                            <div className="flex items-center gap-1 mt-1.5 pt-1.5" style={{ borderTop: '1px dashed #fca5a5' }}>
                                                <span className="text-[10px] text-gray-500 font-medium">Last week:</span>
                                                <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300">
                                                    {lastWeek !== null ? lastWeek : '—'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}

                        </div>

                        {/* Open Ticket Ageing — bar chart, fully dynamic */}
                        {data.open_ticket_ageing && Object.keys(data.open_ticket_ageing).length > 0 && (() => {
                            const URGENCY_PALETTE = [
                                '#22c55e', // green  – freshest
                                '#84cc16', // lime
                                '#eab308', // yellow
                                '#f97316', // orange
                                '#ef4444', // red
                                '#b91c1c', // deep red – most aged
                            ];
                            const ageing = data.open_ticket_ageing!;
                            const sortedKeys = Object.keys(ageing).sort((a, b) => {
                                const numA = parseFloat(a.replace(/[^0-9.]/g, '')) || 0;
                                const numB = parseFloat(b.replace(/[^0-9.]/g, '')) || 0;
                                return numA - numB;
                            });
                            const chartData = sortedKeys.map((key, idx) => ({
                                bucket: key,
                                count: ageing[key] ?? 0,
                                color: URGENCY_PALETTE[Math.min(idx, URGENCY_PALETTE.length - 1)],
                            }));
                            const totalOpen = chartData.reduce((s, b) => s + b.count, 0);
                            const criticalBuckets = chartData.slice(-2);
                            const critical = criticalBuckets.reduce((s, b) => s + b.count, 0);
                            const mostUrgent = [...chartData].reverse().find(b => b.count > 0);

                            // Custom tooltip
                            const AgingTooltip = ({ active, payload }: any) => {
                                if (!active || !payload?.length) return null;
                                const d = payload[0].payload;
                                const pct = totalOpen > 0 ? ((d.count / totalOpen) * 100).toFixed(1) : '0';
                                return (
                                    <div style={{
                                        background: '#fff',
                                        border: `2px solid ${d.color}`,
                                        borderRadius: 10,
                                        padding: '8px 14px',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                                        minWidth: 120,
                                    }}>
                                        <p style={{ fontSize: 12, fontWeight: 700, color: d.color, margin: 0, marginBottom: 4 }}>{d.bucket}</p>
                                        <p style={{ fontSize: 20, fontWeight: 800, color: '#111', margin: 0, lineHeight: 1 }}>{d.count}</p>
                                        <p style={{ fontSize: 11, color: '#6b7280', margin: 0, marginTop: 2 }}>{pct}% of open tickets</p>
                                    </div>
                                );
                            };

                            return (
                                <div className="rounded-xl overflow-hidden mb-6" style={{ border: '1px solid #fed7aa', boxShadow: '0 2px 12px rgba(249,115,22,0.08)' }}>
                                    {/* Header */}
                                    <div className="flex items-center justify-between px-5 py-3" style={{ background: 'linear-gradient(135deg,#fff7ed 0%,#fef3c7 100%)', borderBottom: '1px solid #fde68a' }}>
                                        <div className="flex items-center gap-2.5">
                                            <div className="flex items-center justify-center w-8 h-8 rounded-lg" style={{ background: 'linear-gradient(135deg,#f97316,#ef4444)' }}>
                                                <HourglassOutlined style={{ color: '#fff', fontSize: 15 }} />
                                            </div>
                                            <div>
                                                <h2 className="text-sm font-bold text-gray-800 mb-0">Open Ticket Ageing</h2>
                                                <p className="text-xs text-orange-600 font-medium">{totalOpen} open ticket{totalOpen !== 1 ? 's' : ''} awaiting resolution</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {critical > 0 && (
                                                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full" style={{ background: '#fef2f2', border: '1px solid #fecaca' }}>
                                                    <FireOutlined style={{ color: '#b91c1c', fontSize: 11 }} />
                                                    <span style={{ color: '#b91c1c', fontSize: 11, fontWeight: 700 }}>{critical} critical</span>
                                                </div>
                                            )}
                                            {mostUrgent && (
                                                <div className="text-xs text-gray-500 font-medium hidden sm:block">
                                                    Most aged: <span style={{ color: mostUrgent.color, fontWeight: 700 }}>{mostUrgent.bucket}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Chart body */}
                                    <div className="bg-white px-4 pt-5 pb-3">
                                        {/* Stacked proportion strip */}
                                        <div className="flex h-2 rounded-full overflow-hidden mb-5" style={{ background: '#f3f4f6' }}>
                                            {chartData.map(b => b.count > 0 && (
                                                <div
                                                    key={b.bucket}
                                                    title={`${b.bucket}: ${b.count}`}
                                                    style={{
                                                        width: `${(b.count / totalOpen) * 100}%`,
                                                        background: b.color,
                                                        minWidth: 3,
                                                        transition: 'width 0.8s ease',
                                                    }}
                                                />
                                            ))}
                                        </div>

                                        {/* Bar chart */}
                                        <div style={{ height: 200 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart
                                                    data={chartData}
                                                    margin={{ top: 24, right: 12, left: -20, bottom: 0 }}
                                                    barCategoryGap="22%"
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                                    <XAxis
                                                        dataKey="bucket"
                                                        tick={{ fontSize: 11, fontWeight: 600, fill: '#6b7280' }}
                                                        tickLine={false}
                                                        axisLine={false}
                                                    />
                                                    <YAxis
                                                        tick={{ fontSize: 10, fill: '#9ca3af' }}
                                                        tickLine={false}
                                                        axisLine={false}
                                                        width={28}
                                                    />
                                                    <Tooltip content={<AgingTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)', radius: 6 }} />
                                                    <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={64}>
                                                        {chartData.map((entry, idx) => (
                                                            <Cell key={entry.bucket} fill={entry.color} />
                                                        ))}
                                                        <LabelList
                                                            dataKey="count"
                                                            position="top"
                                                            style={{ fontSize: 12, fontWeight: 800, fill: '#374151' }}
                                                        />
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>

                                        {/* Legend strip */}
                                        <div className="flex flex-wrap gap-2 mt-3 pt-3" style={{ borderTop: '1px solid #f3f4f6' }}>
                                            {chartData.map(b => {
                                                const pct = totalOpen > 0 ? ((b.count / totalOpen) * 100).toFixed(0) : '0';
                                                return (
                                                    <div key={b.bucket} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full" style={{ background: b.color + '18', border: `1px solid ${b.color}40` }}>
                                                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: b.color }} />
                                                        <span style={{ fontSize: 11, color: '#374151', fontWeight: 600 }}>{b.bucket}</span>
                                                        <span style={{ fontSize: 11, color: b.color, fontWeight: 700 }}>{b.count}</span>
                                                        <span style={{ fontSize: 10, color: '#9ca3af' }}>({pct}%)</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* By Status, By Source, and By Department side by side */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                                <h2 className="text-sm font-bold text-gray-800 dark:text-white mb-1">By Status</h2>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">Distribution of all tickets by resolution status</p>
                                {statusEntries.map(([status, count]) => (
                                    <div
                                        key={status}
                                        onClick={() => navigateToStatusByLabel(status)}
                                        className="cursor-pointer rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 -mx-2 px-2 mb-3 last:mb-0 transition-colors duration-150"
                                        title={`Filter by ${status}`}
                                    >
                                        <HBar label={status} value={count} max={maxStatus} color={STATUS_COLORS[status] || '#9ca3af'} />
                                    </div>
                                ))}
                            </div>
                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                                <h2 className="text-sm font-bold text-gray-800 dark:text-white mb-1">By Source</h2>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">Ticket volume breakdown by channel of origin</p>
                                {sourceEntries.map(([source, count]) => (
                                    <div
                                        key={source}
                                        onClick={() => navigateToSource(source)}
                                        className="cursor-pointer rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 -mx-2 px-2 mb-3 last:mb-0 transition-colors duration-150"
                                        title={`Filter by ${source}`}
                                    >
                                        <HBar label={source} value={count} max={maxSource}
                                            color={SOURCE_COLORS[source] || '#9ca3af'} icon={SOURCE_ICONS[source]} />
                                    </div>
                                ))}
                            </div>
                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                                <h2 className="text-sm font-bold text-gray-800 dark:text-white mb-1">By Department</h2>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">Ticket volume breakdown by department</p>
                                {departmentEntries.length > 0 ? (
                                    departmentEntries.map(([department, count]) => (
                                        <div
                                            key={department}
                                            onClick={() => navigateToDepartment(department)}
                                            className="cursor-pointer rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 -mx-2 px-2 mb-3 last:mb-0 transition-colors duration-150"
                                            title={`Filter by ${department}`}
                                        >
                                            <HBar label={department} value={Number(count)} max={maxDepartment} color="#6366f1" />
                                        </div>
                                    ))
                                ) : (
                                    <span className="text-gray-400 dark:text-gray-600 text-xs">No department data</span>
                                )}
                            </div>
                        </div>

                        {/* Source × Status matrix table */}
                        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 mb-6">
                            <h2 className="text-sm font-bold text-gray-800 dark:text-white mb-1">Source × Status Matrix</h2>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">Breakdown of tickets by both source and status</p>
                            <Table
                                dataSource={matrixData}
                                columns={matrixColumns}
                                pagination={false}
                                size="small"
                                scroll={{ x: 'max-content' }}
                                rowClassName={(_, idx) => `${idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}`}
                                className="dark:bg-gray-800"
                            />
                        </div>

                        {/* By L1 Category */}
                        {l1CategoryEntries.length > 0 && (
                            <div className="rounded-xl border border-red-200 dark:border-red-900 shadow-sm mb-6 overflow-hidden">
                                {/* Danger-zone header */}
                                <div className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-red-600 to-rose-500">
                                    <span className="w-2 h-2 rounded-full bg-white opacity-80 animate-pulse" />
                                    <h2 className="text-xs font-bold text-white tracking-wide uppercase">Freshdesk Tickets By Category</h2>
                                    <span className="ml-auto text-[10px] text-red-100 font-medium">{l1CategoryEntries.length} categories · {l1CategoryEntries.reduce((s, c) => s + c.ticket_count, 0)} tickets</span>
                                </div>
                                {/* Horizontal scroll strip */}
                                <div className="bg-white dark:bg-gray-800 px-3 py-3 overflow-x-auto">
                                    <div className="flex gap-2.5" style={{ width: 'max-content' }}>
                                        {l1CategoryEntries.map((cat) => (
                                            <div
                                                key={cat.l1_category_code}
                                                onClick={() => navigateToCategory(cat.l1_category_code)}
                                                className="relative group flex flex-col items-center rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 overflow-hidden shadow-sm hover:border-red-400 hover:shadow-md transition-all duration-200 cursor-pointer"
                                                style={{ width: 76, flexShrink: 0 }}
                                            >
                                                {/* Image area */}
                                                <div className="relative w-full overflow-hidden bg-white dark:bg-gray-800" style={{ height: 62 }}>
                                                    <img
                                                        src={`https://cdn.moglix.com/${cat.l1_category_image_url}`}
                                                        alt={cat.l1_category_name}
                                                        className="w-full h-full object-contain p-1.5 group-hover:scale-110 transition-transform duration-300"
                                                        onError={(e) => {
                                                            (e.target as HTMLImageElement).src = 'https://cdn.moglix.com/cms/error-images/errorImage.jpg';
                                                        }}
                                                    />
                                                    {/* Red ticket count badge */}
                                                    <span className="absolute top-1 right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-600 text-white text-[9px] font-bold px-1 shadow leading-none">
                                                        {cat.ticket_count}
                                                    </span>
                                                </div>
                                                {/* Name */}
                                                <div className="w-full px-1 py-1 text-center">
                                                    <p className="text-[9px] font-semibold text-gray-600 dark:text-gray-300 leading-tight line-clamp-2">
                                                        {cat.l1_category_name}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Ticket Trends — Stacked Area Chart + Donut */}
                        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
                                <div>
                                    <h2 className="text-sm font-bold text-gray-800 dark:text-white mb-0.5">Ticket Trends by Source</h2>
                                    <p className="text-xs text-gray-400 dark:text-gray-500">
                                        {data.interval ? `Stacked by ${data.interval}` : 'Volume over time'} · {dateEntries.reduce((s, [, v]) => s + v.total, 0).toLocaleString()} total tickets
                                    </p>
                                </div>
                                {/* Source legend pills */}
                                <div className="flex flex-wrap gap-1.5">
                                    {['Email', 'Phone', 'Portal', 'Facebook', 'Outbound Email'].map(s => (
                                        <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: (SOURCE_COLORS[s] || '#9ca3af') + '22', color: SOURCE_COLORS[s] || '#9ca3af', border: `1px solid ${SOURCE_COLORS[s] || '#9ca3af'}44` }}>
                                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: SOURCE_COLORS[s] || '#9ca3af' }} />
                                            {s}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Full-width stacked area chart */}
                            <div style={{ height: 280 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart
                                        data={dateEntries.map(([date, val]) => ({
                                            label: formatBucketLabel(date, data.interval),
                                            Total: val.total,
                                            ...(['Email', 'Phone', 'Portal', 'Facebook', 'Outbound Email'].reduce((acc, s) => ({ ...acc, [s]: val.by_source[s]?.total_count || 0 }), {} as Record<string, number>)),
                                        }))}
                                        margin={{ top: 8, right: 16, left: -12, bottom: 0 }}
                                    >
                                        <defs>
                                            {['Email', 'Phone', 'Portal', 'Facebook', 'Outbound Email'].map(s => (
                                                <linearGradient key={s} id={`grad-${s.replace(/\s/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor={SOURCE_COLORS[s] || '#9ca3af'} stopOpacity={0.6} />
                                                    <stop offset="95%" stopColor={SOURCE_COLORS[s] || '#9ca3af'} stopOpacity={0.04} />
                                                </linearGradient>
                                            ))}
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                                        <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} width={32} />
                                        <Tooltip
                                            contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e7eb', boxShadow: '0 4px 16px rgba(0,0,0,0.10)' }}
                                            labelStyle={{ fontWeight: 700, marginBottom: 4, fontSize: 12 }}
                                            itemStyle={{ padding: '2px 0' }}
                                        />
                                        {['Email', 'Phone', 'Portal', 'Facebook', 'Outbound Email'].map(s => (
                                            <Area
                                                key={s}
                                                type="monotone"
                                                dataKey={s}
                                                stackId="1"
                                                stroke={SOURCE_COLORS[s] || '#9ca3af'}
                                                strokeWidth={2}
                                                fill={`url(#grad-${s.replace(/\s/g, '')})`}
                                            />
                                        ))}
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Source totals strip */}
                            <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                                {['Email', 'Phone', 'Portal', 'Facebook', 'Outbound Email'].map(s => {
                                    const total = dateEntries.reduce((sum, [, v]) => sum + (v.by_source[s]?.total_count || 0), 0);
                                    const grand = dateEntries.reduce((sum, [, v]) => sum + v.total, 0);
                                    if (!total) return null;
                                    return (
                                        <div key={s} className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: (SOURCE_COLORS[s] || '#9ca3af') + '14', border: `1px solid ${SOURCE_COLORS[s] || '#9ca3af'}30` }}>
                                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: SOURCE_COLORS[s] || '#9ca3af' }} />
                                            <span className="text-[11px] text-gray-600 dark:text-gray-300 font-medium">{s}</span>
                                            <span className="text-[11px] font-bold" style={{ color: SOURCE_COLORS[s] || '#9ca3af' }}>{total.toLocaleString()}</span>
                                            <span className="text-[10px] text-gray-400">({grand ? ((total / grand) * 100).toFixed(0) : 0}%)</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </StitchLayout >
    );
};

export default CrmDashboard;